import { ChannelWecomGroup } from '../graphql/queries/getCompleteChannelInfo';
import { WecomGroupResolveResult } from '../graphql/queries/resolveWecomGroup';

/** 抽屉内企微群卡片的本地状态 */
export interface WecomGroupItem {
    /** 列表渲染 key */
    key: string;
    groupId: string;
    groupName: string;
    /** 是否为已保存的群（群ID置灰不可编辑） */
    saved: boolean;
    /** 校验错误文案的 i18n key */
    errorKey?: string;
    /** 错误文案的插值参数（如已绑定的客户名称） */
    errorParams?: Record<string, string>;
}

let keySeed = 0;
export const nextWecomGroupKey = () => `wecom-${Date.now()}-${keySeed++}`;

/** 后端返回的已绑定企微群 → 卡片状态 */
export const toWecomGroupItems = (groups?: ChannelWecomGroup[] | null): WecomGroupItem[] =>
    (groups ?? []).map((g) => ({
        key: g.id ? `saved-${g.id}` : nextWecomGroupKey(),
        groupId: g.groupId,
        groupName: g.groupName,
        saved: true,
    }));

export const createEmptyWecomGroupItem = (): WecomGroupItem => ({
    key: nextWecomGroupKey(),
    groupId: '',
    groupName: '',
    saved: false,
});

/**
 * 企微群校验文案的 i18n key，与后端 api-server `MessageKey.CHANNEL_WECOM_GROUP_*` 一致：
 * 前端卡片校验与后端保存校验（如并发下群已被其他客户绑定）共用同一组文案
 */
export const WECOM_GROUP_ERROR_KEYS = {
    idRequired: 'errors.business.channel.wecomGroupIdRequired',
    duplicate: 'errors.business.channel.wecomGroupIdDuplicate',
    boundToOtherChannel: 'errors.business.channel.wecomGroupBoundToOtherChannel',
    notFound: 'errors.business.channel.wecomGroupNotFound',
} as const;

const WECOM_GROUP_SERVER_MESSAGE_KEY_PREFIX = 'errors.business.channel.wecomGroup';

/** 后端返回的 messageKey 是否为企微群校验失败，用于切到企微群所在的联系人 tab */
export const isWecomGroupServerError = (messageKey: unknown): boolean =>
    typeof messageKey === 'string' && messageKey.startsWith(WECOM_GROUP_SERVER_MESSAGE_KEY_PREFIX);

/**
 * 按企微群 ID 查询的结果 → 卡片的群名称与错误：已绑定其他客户优先，其次查不到群名称。
 *
 * @param previousName 查不到时保留的原名称（已保存的群）
 */
export const toResolvedPatch = (
    result: WecomGroupResolveResult | null | undefined,
    previousName = ''
): Pick<WecomGroupItem, 'groupName' | 'errorKey' | 'errorParams'> => {
    if (result?.boundChannelName) {
        return {
            groupName: previousName,
            errorKey: WECOM_GROUP_ERROR_KEYS.boundToOtherChannel,
            errorParams: { channelName: result.boundChannelName },
        };
    }
    // 群名称为 null 才是查不到；空串表示对方有这个群但还没记到群名，按返回原样展示
    const groupName = result?.groupName;
    if (groupName == null) {
        return { groupName: previousName, errorKey: WECOM_GROUP_ERROR_KEYS.notFound, errorParams: undefined };
    }
    return { groupName, errorKey: undefined, errorParams: undefined };
};

/**
 * 点击「确认」时校验全部卡片（与后端一致：提交的群全部按新增的标准重新校验），按顺序：
 * 群ID不能为空 → 未保存的卡片不能与其它卡片重复 → 不能已绑定其他客户 → 必须查到群名称；查到的新名称回写到卡片。
 *
 * @param resolveGroup 按群ID查询群名称与绑定情况
 * @returns 带错误信息的卡片列表，以及是否全部通过
 */
export const validateWecomGroups = async (
    items: WecomGroupItem[],
    resolveGroup: (groupId: string) => Promise<WecomGroupResolveResult | null>
): Promise<{ items: WecomGroupItem[]; valid: boolean }> => {
    const idCount = new Map<string, number>();
    items.forEach((item) => {
        const id = item.groupId.trim();
        if (id) {
            idCount.set(id, (idCount.get(id) ?? 0) + 1);
        }
    });

    const checked: WecomGroupItem[] = [];
    for (const item of items) {
        const groupId = item.groupId.trim();
        if (!groupId) {
            checked.push({ ...item, groupId, errorKey: WECOM_GROUP_ERROR_KEYS.idRequired, errorParams: undefined });
            continue;
        }
        // 重复时只标记未保存的卡片：已保存的群本身不会重复，重复一定是新增卡片造成的
        if (!item.saved && (idCount.get(groupId) ?? 0) > 1) {
            checked.push({ ...item, groupId, errorKey: WECOM_GROUP_ERROR_KEYS.duplicate, errorParams: undefined });
            continue;
        }
        checked.push({ ...item, groupId, ...toResolvedPatch(await resolveGroup(groupId), item.groupName) });
    }
    return { items: checked, valid: checked.every((item) => !item.errorKey) };
};

/**
 * 计算提交给后端的 wecomGroupIds（全量快照）；返回 undefined 表示本次不修改企微群。
 * 编辑客户时只有已成功加载已绑定的群才传，避免加载失败时传空列表把已绑定的群全部删除；新建客户只有添加了群才传。
 */
export const buildWecomGroupIds = (
    items: WecomGroupItem[],
    mode: 'create' | 'edit',
    loaded: boolean
): string[] | undefined => {
    const shouldSubmit = mode === 'edit' ? loaded : items.length > 0;
    return shouldSubmit ? items.map((item) => item.groupId.trim()) : undefined;
};
